// ─────────────────────────────────────────────────────────────────────────────
// Player.jsx
//
// The Ambient Player. Two screens, rendered by the same component:
//
//   1. Library:  grid of pieces, filters, tier-gated access.
//   2. Playback: embedded <App /> running in kiosk mode with a Player-level
//                overlay (pause, skip, stop, fullscreen, volume, time).
//
// Why we embed the Toolkit rather than write a new playback engine:
//   The audio is the differentiator. The Toolkit's column-scan audio is
//   provably derived from the math, which is the sales story for studios.
//   A separate audio engine would sever that story. Embedding the Toolkit
//   keeps the math-driven audio while wrapping it in a simplified UI.
//
// Why we use URL params (?preset=X&kiosk=true) rather than props:
//   The Toolkit reads its initial state from URL params (existing pattern
//   for ?setup=1). Adding ?preset and ?kiosk extends that pattern without
//   restructuring how the Toolkit initializes.
//
// User gesture context for audio:
//   When the user clicks Play on a library card, that click is a user
//   gesture. We switch to playback mode within the same React tree (no
//   page navigation), so the gesture context is preserved and the
//   Toolkit's Web Audio context can start.
// ─────────────────────────────────────────────────────────────────────────────

import React, { useEffect, useMemo, useRef, useState } from "react";
import App from "./App";
import {
  PLAYER_LIBRARY,
  MOOD_OPTIONS,
  CLASS_TYPE_OPTIONS,
  DURATION_OPTIONS,
  filterLibrary,
} from "./playerLibrary";
import { useUserTier, useCurrentUser, TIERS } from "./userTier";

const DEFAULT_DURATION_MIN = 45;


export default function Player() {
  const tier = useUserTier();
  const user = useCurrentUser();
  const hasStudio = tier === TIERS.STUDIO;

  const [filters, setFilters] = useState({ mood: "all", classType: "all", duration: 0 });
  const [playingPiece, setPlayingPiece] = useState(null);
  const [playbackDuration, setPlaybackDuration] = useState(DEFAULT_DURATION_MIN);

  const visiblePieces = useMemo(
    () => filterLibrary(PLAYER_LIBRARY, filters),
    [filters]
  );

  // ─── Update URL when playback stops (back to library) ──────────────────────
  // When starting playback, the URL is updated synchronously by the Play
  // click handler so that App's mount can read the kiosk + preset params.
  // When stopping, we clear the params here.
  useEffect(() => {
    if (playingPiece) return;  // start-of-playback handled synchronously below
    const url = new URL(window.location.href);
    if (url.searchParams.has("preset") || url.searchParams.has("kiosk")) {
      url.searchParams.delete("preset");
      url.searchParams.delete("kiosk");
      window.history.replaceState({}, "", url.toString());
    }
  }, [playingPiece]);

  // ─── Browser Back / Forward navigation ─────────────────────────────────────
  // popstate fires when the user clicks the browser's Back or Forward button.
  // We re-read the URL and sync `playingPiece` to it: if `?preset=` is still
  // in the URL, we're on a playback entry; otherwise we're on the library.
  // Without this, Back changes the URL but React state doesn't update, so the
  // user appears stuck on the same screen.
  useEffect(() => {
    function onPopState() {
      const params = new URLSearchParams(window.location.search);
      const presetId = params.get("preset");
      if (presetId) {
        const piece = PLAYER_LIBRARY.find((p) => p.id === presetId);
        setPlayingPiece(piece || null);
      } else {
        setPlayingPiece(null);
      }
    }
    window.addEventListener("popstate", onPopState);
    return () => window.removeEventListener("popstate", onPopState);
  }, []);

  // Handler bound to library cards. Runs *before* state update so that App's
  // first mount sees the correct URL params. Uses pushState (not replaceState)
  // so the browser Back button returns the user to the library.
  function startPlaying(piece) {
    const url = new URL(window.location.href);
    url.searchParams.set("preset", piece.id);
    url.searchParams.set("kiosk", "true");
    window.history.pushState({}, "", url.toString());
    setPlayingPiece(piece);
  }

  // ─── Playback mode: render the Toolkit + Player overlay ────────────────────
  if (playingPiece) {
    return (
      <PlaybackShell
        piece={playingPiece}
        durationMinutes={playbackDuration}
        onStop={() => setPlayingPiece(null)}
      />
    );
  }

  // ─── Library mode ──────────────────────────────────────────────────────────
  return (
    <div style={appStyle}>
      <Header user={user} tier={tier} />

      <div style={contentStyle}>
        <h1 style={titleStyle}>Ambient Player</h1>
        <p style={subtitleStyle}>
          Generative audiovisual pieces for studios, clinics, and meditation
          spaces. Every sound is derived in real time from the mathematics
          of the image you see.
        </p>

        <Filters filters={filters} onChange={setFilters} />
        <DurationSelector value={playbackDuration} onChange={setPlaybackDuration} />

        <div style={gridStyle}>
          {visiblePieces.length === 0 ? (
            <div style={emptyStateStyle}>
              No pieces match these filters. Try widening your selection.
            </div>
          ) : (
            visiblePieces.map((piece) => (
              <PieceCard
                key={piece.id}
                piece={piece}
                locked={!piece.preview && !hasStudio}
                onPlay={() => startPlaying(piece)}
              />
            ))
          )}
        </div>

        <Footer hasStudio={hasStudio} />
      </div>
    </div>
  );
}


// ─── Playback shell: embeds <App /> in kiosk mode + overlay controls ────────

function PlaybackShell({ piece, durationMinutes, onStop }) {
  const containerRef = useRef(null);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [isPaused, setIsPaused] = useState(false);
  const [elapsed, setElapsed] = useState(0);
  const startTimeRef = useRef(performance.now());
  const pausedAccumRef = useRef(0);
  const lastPauseAtRef = useRef(null);

  // ─── Tick the elapsed clock; auto-stop when duration is reached ────────────
  useEffect(() => {
    const id = setInterval(() => {
      if (isPaused) return;
      const now = performance.now();
      const liveElapsedSec = (now - startTimeRef.current - pausedAccumRef.current) / 1000;
      setElapsed(liveElapsedSec);
      if (liveElapsedSec >= durationMinutes * 60) {
        onStop();
      }
    }, 250);
    return () => clearInterval(id);
  }, [durationMinutes, onStop, isPaused]);

  // ─── Fullscreen plumbing ───────────────────────────────────────────────────
  useEffect(() => {
    const onChange = () => setIsFullscreen(!!document.fullscreenElement);
    document.addEventListener("fullscreenchange", onChange);
    return () => document.removeEventListener("fullscreenchange", onChange);
  }, []);

  async function toggleFullscreen() {
    if (document.fullscreenElement) {
      await document.exitFullscreen();
    } else {
      await containerRef.current?.requestFullscreen();
    }
  }

  // ─── Pause / Resume: click the Toolkit's play/pause buttons by ID ──────────
  // The Toolkit's play and pause buttons have IDs (#fractal-play-button,
  // #fractal-pause-button, #fractal-resume-button). We invoke playback by
  // clicking them programmatically — no need to reach into App's internals
  // or dispatch synthetic keyboard events.
  function clickToolkitButton(id) {
    const btn = document.getElementById(id);
    if (btn) btn.click();
  }

  function togglePause() {
    const now = performance.now();
    if (isPaused) {
      if (lastPauseAtRef.current !== null) {
        pausedAccumRef.current += now - lastPauseAtRef.current;
        lastPauseAtRef.current = null;
      }
      clickToolkitButton("fractal-resume-button");
    } else {
      lastPauseAtRef.current = now;
      clickToolkitButton("fractal-pause-button");
    }
    setIsPaused(!isPaused);
  }

  // ─── Skip: stop this piece and pick the next one. For MVP, we just stop. ───
  // A future version can track playlist position and advance. For now, "skip"
  // returns to library so the teacher can pick a different piece.
  function skip() {
    onStop();
  }

  const remaining = Math.max(0, durationMinutes * 60 - elapsed);
  const remMin = Math.floor(remaining / 60);
  const remSec = Math.floor(remaining % 60).toString().padStart(2, "0");

  return (
    <div ref={containerRef} style={playbackContainerStyle}>
      {/* The Toolkit, rendered with preset+kiosk URL params already set */}
      <App />

      {/* Player overlay — pause/skip/stop + status (always visible) */}
      <div style={playbackOverlayStyle}>
        <div style={overlayInnerStyle}>
          <div>
            <div style={overlayTitleStyle}>{piece.title}</div>
            <div style={overlayMetaStyle}>
              {isPaused ? "Paused" : `${remMin}:${remSec} remaining`}
            </div>
          </div>
          <div style={overlayButtonRowStyle}>
            <button onClick={togglePause} style={overlayButtonStyle}>
              {isPaused ? "Resume" : "Pause"}
            </button>
            <button onClick={skip} style={overlayButtonStyle}>Skip</button>
            <button onClick={toggleFullscreen} style={overlayButtonStyle}>
              {isFullscreen ? "Exit fullscreen" : "Fullscreen"}
            </button>
            <button onClick={onStop} style={overlayStopButtonStyle}>Stop</button>
          </div>
        </div>
      </div>
    </div>
  );
}


// ─── Header ─────────────────────────────────────────────────────────────────

function Header({ user, tier }) {
  return (
    <div style={headerStyle}>
      <a href="/" style={backLinkStyle}>← Toolkit</a>
      {user ? (
        <div style={userInfoStyle}>
          <span style={{ opacity: 0.7 }}>{user.email}</span>
          <span style={tierPillStyle}>{tier.toUpperCase()}</span>
        </div>
      ) : (
        <a href="/" style={backLinkStyle}>Sign in via Toolkit</a>
      )}
    </div>
  );
}


// ─── Filter controls ────────────────────────────────────────────────────────

function Filters({ filters, onChange }) {
  return (
    <div style={filterRowStyle}>
      <FilterSelect
        label="Mood" value={filters.mood} options={MOOD_OPTIONS}
        onChange={(v) => onChange({ ...filters, mood: v })}
      />
      <FilterSelect
        label="Class type" value={filters.classType} options={CLASS_TYPE_OPTIONS}
        onChange={(v) => onChange({ ...filters, classType: v })}
      />
      <FilterSelect
        label="Duration" value={filters.duration} options={DURATION_OPTIONS}
        onChange={(v) => onChange({ ...filters, duration: Number(v) })}
        valueIsNumber
      />
    </div>
  );
}

function FilterSelect({ label, value, options, onChange, valueIsNumber }) {
  return (
    <label style={{ display: "flex", flexDirection: "column", gap: 6 }}>
      <span style={filterLabelStyle}>{label}</span>
      <select
        value={value}
        onChange={(e) => onChange(valueIsNumber ? Number(e.target.value) : e.target.value)}
        style={selectStyle}
      >
        {options.map((opt) => (
          <option key={String(opt.value)} value={opt.value}>{opt.label}</option>
        ))}
      </select>
    </label>
  );
}


// ─── Duration selector ──────────────────────────────────────────────────────

function DurationSelector({ value, onChange }) {
  return (
    <div style={durationBarStyle}>
      <span style={filterLabelStyle}>Play for:</span>
      {[20, 45, 60, 90].map((mins) => (
        <button
          key={mins}
          onClick={() => onChange(mins)}
          style={{
            ...durationButtonStyle,
            ...(value === mins ? durationButtonActiveStyle : null),
          }}
        >
          {mins} min
        </button>
      ))}
    </div>
  );
}


// ─── Library card (no live thumbnail — the Toolkit-quality render is too
//     heavy to do at small size; we use a static colored gradient instead.
//     Future: replace with cached thumbnails rendered offline.) ─────────────

function PieceCard({ piece, locked, onPlay }) {
  return (
    <div style={cardStyle}>
      <div style={{ position: "relative" }}>
        <div style={{ ...thumbStyle, background: thumbGradientFor(piece) }} />
        {locked && (
          <div style={lockedOverlayStyle}>
            <div style={{ fontSize: 14, marginBottom: 6, opacity: 0.85 }}>Studio</div>
            <div style={{ fontSize: 13, opacity: 0.7 }}>Upgrade to unlock</div>
          </div>
        )}
      </div>
      <div style={cardBodyStyle}>
        <div style={cardTitleStyle}>{piece.title}</div>
        <div style={cardDescStyle}>{piece.description}</div>
        <div style={cardMetaStyle}>
          {piece.mood} · {piece.classTypes.slice(0, 2).join(", ")}
        </div>
        <button
          onClick={onPlay}
          disabled={locked}
          style={{
            ...playButtonStyle,
            ...(locked ? playButtonDisabledStyle : null),
          }}
        >
          {locked ? "Studio only" : "Play"}
        </button>
      </div>
    </div>
  );
}

// Build a placeholder CSS gradient evoking the piece's mood/palette. This is
// just for the card thumbnail until we wire up real thumbnails.
function thumbGradientFor(piece) {
  const palettes = {
    classic: "linear-gradient(135deg, #000764 0%, #20a0cb 45%, #ffd28a 75%, #4a0e0e 100%)",
    ocean:   "linear-gradient(135deg, #001020 0%, #004080 40%, #28b4dc 75%, #b4f0ff 100%)",
    fire:    "linear-gradient(135deg, #000 0%, #780000 40%, #dc3c00 70%, #ffb400 100%)",
    rainbow: "linear-gradient(135deg, #9400d3 0%, #0000ff 35%, #00ff00 60%, #ffff00 85%, #ff7f00 100%)",
    grayscale: "linear-gradient(135deg, #000 0%, #fff 100%)",
  };
  return palettes[piece.spec.paletteName] || palettes.classic;
}


// ─── Footer ─────────────────────────────────────────────────────────────────

function Footer({ hasStudio }) {
  if (hasStudio) return null;
  return (
    <div style={footerStyle}>
      <strong style={{ display: "block", marginBottom: 6 }}>
        Get the full library
      </strong>
      The Studio tier unlocks every piece and new additions each month.
      Currently {PLAYER_LIBRARY.length} pieces; growing.
    </div>
  );
}


// ─── Styles ─────────────────────────────────────────────────────────────────

const appStyle = {
  minHeight: "100vh",
  background: "#0a0e1a",
  color: "#e2e8f0",
  fontFamily: "system-ui, -apple-system, sans-serif",
};

const headerStyle = {
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  padding: "16px 32px",
  borderBottom: "1px solid #1e293b",
};

const backLinkStyle = {
  color: "#94a3b8",
  textDecoration: "none",
  fontSize: 14,
};

const userInfoStyle = {
  display: "flex",
  alignItems: "center",
  gap: 12,
  fontSize: 13,
};

const tierPillStyle = {
  background: "#1e293b",
  color: "#60a5fa",
  fontSize: 11,
  textTransform: "uppercase",
  letterSpacing: "0.08em",
  padding: "2px 8px",
  borderRadius: 10,
};

const contentStyle = {
  maxWidth: 1200,
  margin: "0 auto",
  padding: "40px 32px 80px",
};

const titleStyle = {
  fontSize: 36,
  margin: "0 0 8px 0",
  fontWeight: 400,
};

const subtitleStyle = {
  fontSize: 16,
  color: "#94a3b8",
  margin: "0 0 32px 0",
  maxWidth: 720,
  lineHeight: 1.5,
};

const filterRowStyle = {
  display: "flex",
  flexWrap: "wrap",
  gap: 20,
  marginBottom: 24,
};

const filterLabelStyle = {
  fontSize: 11,
  textTransform: "uppercase",
  letterSpacing: "0.1em",
  color: "#94a3b8",
};

const selectStyle = {
  background: "#1e293b",
  color: "#e2e8f0",
  border: "1px solid #334155",
  borderRadius: 6,
  padding: "8px 12px",
  fontSize: 14,
  minWidth: 160,
  fontFamily: "inherit",
};

const durationBarStyle = {
  display: "flex",
  alignItems: "center",
  gap: 10,
  marginBottom: 32,
  flexWrap: "wrap",
};

const durationButtonStyle = {
  background: "transparent",
  color: "#cbd5e1",
  border: "1px solid #334155",
  borderRadius: 6,
  padding: "8px 16px",
  fontSize: 14,
  cursor: "pointer",
  fontFamily: "inherit",
};

const durationButtonActiveStyle = {
  background: "#1e293b",
  color: "#60a5fa",
  borderColor: "#60a5fa",
};

const gridStyle = {
  display: "grid",
  gridTemplateColumns: "repeat(auto-fill, minmax(320px, 1fr))",
  gap: 20,
};

const cardStyle = {
  background: "#0f172a",
  border: "1px solid #1e293b",
  borderRadius: 10,
  overflow: "hidden",
  display: "flex",
  flexDirection: "column",
};

const thumbStyle = {
  width: "100%",
  height: 180,
  display: "block",
};

const lockedOverlayStyle = {
  position: "absolute",
  inset: 0,
  background: "rgba(10, 14, 26, 0.7)",
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  justifyContent: "center",
  color: "#e2e8f0",
  backdropFilter: "blur(4px)",
};

const cardBodyStyle = {
  padding: 18,
  display: "flex",
  flexDirection: "column",
  gap: 8,
};

const cardTitleStyle = {
  fontSize: 18,
  fontWeight: 500,
};

const cardDescStyle = {
  fontSize: 14,
  color: "#cbd5e1",
  lineHeight: 1.4,
};

const cardMetaStyle = {
  fontSize: 12,
  color: "#64748b",
  textTransform: "capitalize",
};

const playButtonStyle = {
  marginTop: 8,
  background: "#60a5fa",
  color: "#0f172a",
  border: "none",
  borderRadius: 6,
  padding: "10px 16px",
  fontSize: 14,
  fontWeight: 600,
  cursor: "pointer",
  fontFamily: "inherit",
};

const playButtonDisabledStyle = {
  background: "#1e293b",
  color: "#64748b",
  cursor: "not-allowed",
};

const emptyStateStyle = {
  gridColumn: "1 / -1",
  padding: "40px 20px",
  textAlign: "center",
  color: "#64748b",
  fontSize: 15,
  background: "#0f172a",
  border: "1px dashed #1e293b",
  borderRadius: 10,
};

const footerStyle = {
  marginTop: 40,
  padding: 20,
  background: "#0f172a",
  border: "1px solid #1e293b",
  borderRadius: 10,
  fontSize: 14,
  color: "#cbd5e1",
};

const playbackContainerStyle = {
  position: "fixed",
  inset: 0,
  background: "#000",
  zIndex: 100,
  overflow: "hidden",
};

const playbackOverlayStyle = {
  position: "absolute",
  top: 0,
  left: 0,
  right: 0,
  padding: "16px 28px",
  background: "linear-gradient(to bottom, rgba(0,0,0,0.55) 0%, rgba(0,0,0,0) 100%)",
  color: "#e2e8f0",
  zIndex: 200,
};

const overlayInnerStyle = {
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  gap: 20,
  flexWrap: "wrap",
};

const overlayTitleStyle = {
  fontSize: 20,
  fontWeight: 500,
};

const overlayMetaStyle = {
  fontSize: 13,
  opacity: 0.7,
  marginTop: 4,
};

const overlayButtonRowStyle = {
  display: "flex",
  gap: 10,
  flexWrap: "wrap",
};

const overlayButtonStyle = {
  background: "rgba(255,255,255,0.1)",
  color: "#fff",
  border: "1px solid rgba(255,255,255,0.2)",
  borderRadius: 6,
  padding: "10px 18px",
  fontSize: 14,
  cursor: "pointer",
  fontFamily: "inherit",
  backdropFilter: "blur(4px)",
};

const overlayStopButtonStyle = {
  ...overlayButtonStyle,
  background: "rgba(220, 38, 38, 0.5)",
  borderColor: "rgba(220, 38, 38, 0.7)",
};
